/**
 * How a group that UNLOCKS AT A LEVEL is named to a member, in one place.
 *
 * Such a group is EARNED: the dashboard sets it to open at a gamification
 * level, and the bridge sends `pricing_type: "level"` with `unlock_level` /
 * `unlock_level_name`, plus `locked` for this viewer. It has no price and no
 * join to attempt, so every surface that would otherwise show a price band
 * shows the level instead.
 *
 * The words are the dashboard's own — Free is "Free", Paid is "Paid", this is
 * "Unlock at level 3" — so a creator reads the same sentence in both apps.
 *
 * Its own module, not a component helper: the groups page, Home and the group
 * landing all ask this and had already drifted apart once.
 */

/** The level's name, but only when it says more than "Level 3" already does. */
const distinctName = (g) => {
  const name = String(g?.unlock_level_name || "").trim();
  if (!name) return "";
  const plain = name.replace(/\s+/g, " ").toLowerCase();
  return plain === `level ${g.unlock_level}`.toLowerCase() ? "" : name;
};

/**
 * Does this group COME WITH A COURSE? The bridge sets `course_linked` when the
 * dashboard attached it to a course product: free of charge, but the course's
 * buyers are the only way in, so it is never labelled "Free" either.
 */
export const isCourseLinkedGroup = (g) => g?.course_linked === true;

/** Is this group earned rather than free or bought? */
export const isLevelGroup = (g) =>
  (g?.pricing_type || "") === "level" || Number(g?.unlock_level) > 0;

/** The band label: "Comes with course", "Unlock at level 3 · Gold", … */
export const groupLevelLabel = (g) => {
  if (isCourseLinkedGroup(g)) return "Comes with course";
  if (!g?.unlock_level) return "Unlock at a level";
  const name = distinctName(g);
  return `Unlock at level ${g.unlock_level}${name ? ` · ${name}` : ""}`;
};

/**
 * What to say when a member who has not earned it tries to open it — or null
 * when the group may be opened. There is nothing to buy, so this sentence is
 * the whole answer rather than a step towards a checkout.
 */
export const groupLevelGate = (g) => {
  /*
   * A COURSE-LINKED group is deliberately not handled here. There IS something
   * to buy — the course — and the card already routes a locked group with an
   * unlock product to that checkout. Intercepting it with a message would stop
   * the member reaching the thing that would let them in.
   */
  if (!isLevelGroup(g) || isCourseLinkedGroup(g) || !g?.locked) return null;
  const name = distinctName(g) || (g.unlock_level ? `level ${g.unlock_level}` : "a higher level");
  return `Reach ${name} to unlock this community.`;
};
